import { currencySchema } from '@btsis/core';
import { oc } from '@orpc/contract';
import { z } from 'zod';
import { commonErrors } from './common/errors.js';
import { listRequestSchema } from './common/list.js';
import { minorAmountSchema, moneySchema } from './common/money.js';
import { paginated, paginationInputSchema } from './common/pagination.js';
import { organizationFiguresSchema } from './organization-figures.js';

const guarded = {
  UNAUTHENTICATED: commonErrors.UNAUTHENTICATED,
  PERMISSION_DENIED: commonErrors.PERMISSION_DENIED,
  TWO_FACTOR_REQUIRED: commonErrors.TWO_FACTOR_REQUIRED,
  OUT_OF_SCOPE: commonErrors.OUT_OF_SCOPE,
  NOT_FOUND: commonErrors.NOT_FOUND,
  CONFLICT: commonErrors.CONFLICT,
  VALIDATION_FAILED: commonErrors.VALIDATION_FAILED,
};

const isoDaySchema = z.iso.date('a day is a real YYYY-MM-DD date');

/** The widest consolidation one read converts, in days counting both ends: a reporting year. */
export const MAX_CONSOLIDATION_DAYS = 366;

export const financeOrganizationSchema = z.object({
  id: z.uuid(),
  code: z.string(),
  name: z.string(),
  baseCurrency: currencySchema,
});

export type FinanceOrganization = z.infer<typeof financeOrganizationSchema>;

/**
 * One organization's balance in one currency: what its institutions' entries
 * add up to, what sits at organization level — fees, transfers, a refund with
 * no donation — and both together.
 */
export const organizationBalanceSchema = z.object({
  organizationId: z.uuid(),
  organizationCode: z.string(),
  organizationName: z.string(),
  currency: currencySchema,
  institutions: moneySchema,
  organizationLevel: moneySchema,
  total: moneySchema,
  /**
   * What this organization has transferred into institutions' vaults, net of
   * the transfers reversed — a flow, **not** what the vaults hold
   * (docs/decisions/0133 §10). A vault mixes organizations' transfers and is
   * spent from, and spending cannot be attributed to one organization, so this is
   * never a holding: it is not part of `total`, which fell by it when the
   * transfer completed, and it is not added into any holding total.
   */
  vaultTransfers: moneySchema,
});

export type OrganizationBalance = z.infer<typeof organizationBalanceSchema>;

export const institutionBalanceSchema = z.object({
  institutionId: z.uuid(),
  institutionCode: z.string(),
  institutionName: z.string(),
  organizationId: z.uuid(),
  organizationName: z.string(),
  currency: currencySchema,
  balance: moneySchema,
  /**
   * What the institution's vaults hold in this currency: the sum of every entry
   * of its vaults, transfers in, spending out and their reversals — what
   * *Kasalar* shows (docs/decisions/0133 §10). It is the institution's, not this
   * organization's, so the same figure sits on each of the institution's
   * organization rows and is never summed across them or added into `balance`.
   */
  institutionVaults: moneySchema,
});

export type InstitutionBalance = z.infer<typeof institutionBalanceSchema>;

export const balancesInputSchema = paginationInputSchema.extend({
  organizationId: z.uuid().optional(),
  institutionId: z.uuid().optional(),
  asOf: isoDaySchema.optional(),
});

export type BalancesInput = z.infer<typeof balancesInputSchema>;

/**
 * Sums of the ledger up to and including `asOf` — never a stored counter. The
 * organizations are those the reader's scope wholly holds; the institutions,
 * page by page, those inside the reader's scope —
 * docs/decisions/0045-reconciliation-is-scoped-by-the-whole-organization.md.
 */
export const balancesSchema = z.object({
  asOf: isoDaySchema,
  organizations: z.array(organizationBalanceSchema),
  institutions: paginated(institutionBalanceSchema),
});

export type Balances = z.infer<typeof balancesSchema>;

export const LOCATION_GROUPS = ['region', 'zone', 'country'] as const;
export type LocationGroup = (typeof LOCATION_GROUPS)[number];

export const balancesByLocationInputSchema = paginationInputSchema.extend({
  groupBy: z.enum(LOCATION_GROUPS).default('country'),
  regionId: z.uuid().optional(),
  zoneId: z.uuid().optional(),
  countryId: z.uuid().optional(),
});

export type BalancesByLocationInput = z.infer<typeof balancesByLocationInputSchema>;

/**
 * One node of the geographic tree and what the accounts of the organizations
 * placed under it hold, per currency — never summed across currencies
 * (docs/decisions/0127 §2). The unplaced organizations are one group whose
 * `id` and names are null; a zone's `regionName` and a country's `zoneName`
 * and `regionName` say where it sits, and are null above its own level.
 */
export const locationBalanceGroupSchema = z.object({
  id: z.uuid().nullable(),
  name: z.string().nullable(),
  regionName: z.string().nullable(),
  zoneName: z.string().nullable(),
  organizationCount: z.number().int().min(0),
  figures: z.array(
    organizationFiguresSchema.extend({
      /**
       * What the organizations placed here have transferred into institutions'
       * vaults, net of reversals — a flow, not a holding (docs/decisions/0133 §10).
       */
      vaultTransfers: moneySchema,
    }),
  ),
});

export type LocationBalanceGroup = z.infer<typeof locationBalanceGroupSchema>;

export const consolidationInputSchema = z
  .object({ organizationId: z.uuid(), from: isoDaySchema, to: isoDaySchema })
  .refine((input) => input.from <= input.to, 'the period ends after it starts');

export type ConsolidationInput = z.infer<typeof consolidationInputSchema>;

export const consolidatedCurrencySchema = z.object({
  currency: currencySchema,
  original: moneySchema,
  converted: moneySchema.nullable(),
  entryCount: z.number().int(),
  firstRateDate: isoDaySchema.nullable(),
  lastRateDate: isoDaySchema.nullable(),
});

export type ConsolidatedCurrencyView = z.infer<typeof consolidatedCurrencySchema>;

export const consolidationSchema = z.object({
  organization: financeOrganizationSchema,
  from: isoDaySchema,
  to: isoDaySchema,
  currencies: z.array(consolidatedCurrencySchema),
  total: moneySchema,
  unconvertedCurrencies: z.array(currencySchema),
});

export type ConsolidationView = z.infer<typeof consolidationSchema>;

/**
 * Balances and the consolidation report — docs/plans/05-finance-reconciliation.md
 * §5.8 and PRD §10. Read-only, from `ledger_entries` alone.
 */
/**
 * **The inter-institution ledger** — the institution statement screen,
 * docs/decisions/0115. One institution's balance in one currency: the sum of
 * its entries, negative when it owes (it recorded work another institution
 * carried out) and positive when it is owed for work it carried out.
 */
export const institutionLedgerBalanceSchema = z.object({
  institutionId: z.uuid(),
  institutionCode: z.string(),
  institutionName: z.string(),
  countryName: z.string(),
  currency: currencySchema,
  debit: moneySchema,
  credit: moneySchema,
  balance: moneySchema,
  entryCount: z.number().int().nonnegative(),
  lastEntryAt: z.string(),
});

export type InstitutionLedgerBalance = z.infer<typeof institutionLedgerBalanceSchema>;

export const INSTITUTION_BALANCE_SORT_FIELDS = [
  'institutionName',
  'balance',
  'lastEntryAt',
] as const;
export const INSTITUTION_STATEMENT_SORT_FIELDS = ['effectiveAt', 'amount'] as const;
export const institutionLedgerBalancesInputSchema = listRequestSchema.extend({
  sortBy: z.enum(INSTITUTION_BALANCE_SORT_FIELDS).optional(),
  sortDir: z.enum(['asc', 'desc']).optional(),
  currency: currencySchema.optional(),
});

export const INSTITUTION_LEDGER_KINDS = [
  'fulfilment',
  'reversal',
  'collection',
  'collection_reversal',
] as const;
export const INSTITUTION_LEDGER_SIDES = ['recorder', 'fulfiller'] as const;
export const INSTITUTION_LEDGER_CAUSES = [
  'fulfilled',
  'unfulfilled',
  'cancelled',
  'remainder_cancelled',
] as const;
/** Why a confirmed match, and with it its collection, was taken back. */
export const INSTITUTION_COLLECTION_REVERSAL_KINDS = [
  'unmatch',
  'cancel_refund',
  'cancel_reverse',
  'chargeback',
  'provider_refund',
] as const;

/**
 * One line of an institution's statement: when, which donation, how much and in
 * which direction, and the running balance in its currency after it — computed
 * over the institution's whole history, so a filter narrows the lines and never
 * changes a balance.
 *
 * A `fulfilment` or `reversal` is work between two institutions
 * (docs/decisions/0115) and carries its side, cause, counter-institution, units
 * and unit cost, with `collection` null. A `collection` or
 * `collection_reversal` is a confirmed match on the donation's recording
 * institution (docs/decisions/0121) and carries `collection` — the bank line,
 * the organization and account that received it, and why it was reversed —
 * with the fulfilment fields null.
 */
export const institutionLedgerEntrySchema = z.object({
  id: z.uuid(),
  postingId: z.uuid(),
  effectiveAt: z.string(),
  kind: z.enum(INSTITUTION_LEDGER_KINDS),
  side: z.enum(INSTITUTION_LEDGER_SIDES).nullable(),
  cause: z.enum(INSTITUTION_LEDGER_CAUSES).nullable(),
  donationId: z.uuid(),
  referenceCode: z.string(),
  campaignName: z.string(),
  counterInstitutionId: z.uuid().nullable(),
  counterInstitutionName: z.string().nullable(),
  units: z.number().int().positive().nullable(),
  unitCost: moneySchema.nullable(),
  amount: moneySchema,
  balanceAfter: moneySchema,
  collection: z
    .object({
      reconciliationId: z.uuid(),
      reversalKind: z.enum(INSTITUTION_COLLECTION_REVERSAL_KINDS).nullable(),
      accountTransactionId: z.uuid(),
      lineBookedOn: isoDaySchema,
      organizationId: z.uuid(),
      organizationName: z.string(),
      /** Null where the reader's scope does not wholly hold the organization (`organizationInScope`, 0129). */
      accountLabel: z.string().nullable(),
    })
    .nullable(),
});

export type InstitutionLedgerEntry = z.infer<typeof institutionLedgerEntrySchema>;

const timestampSchema = z.iso.datetime({ offset: true });

/**
 * Every filter is server-side. The amount range is on the line's size, debit
 * or credit alike, in the currency's minor units; `direction` narrows to one.
 */
export const institutionLedgerStatementInputSchema = paginationInputSchema.extend({
  sortBy: z.enum(INSTITUTION_STATEMENT_SORT_FIELDS).optional(),
  sortDir: z.enum(['asc', 'desc']).optional(),
  institutionId: z.uuid(),
  currency: currencySchema.optional(),
  from: timestampSchema.optional(),
  to: timestampSchema.optional(),
  referenceCode: z.string().trim().min(1).max(32).optional(),
  counterInstitutionId: z.uuid().optional(),
  direction: z.enum(['debit', 'credit']).optional(),
  minAmountMinor: minorAmountSchema.optional(),
  maxAmountMinor: minorAmountSchema.optional(),
});

export type InstitutionLedgerStatementInput = z.infer<typeof institutionLedgerStatementInputSchema>;

export const institutionLedgerStatementSchema = paginated(institutionLedgerEntrySchema).extend({
  institution: z.object({
    id: z.uuid(),
    code: z.string(),
    name: z.string(),
    countryName: z.string(),
  }),
  /** The institution's balance per currency, over every entry it has. */
  balances: z.array(z.object({ currency: currencySchema, balance: moneySchema })),
  /** The institutions it has a line against, for the counter-institution filter. */
  counterInstitutions: z.array(z.object({ id: z.uuid(), name: z.string() })),
});

export type InstitutionLedgerStatement = z.infer<typeof institutionLedgerStatementSchema>;

/**
 * **An incoming bank movement awaiting reconciliation, classified to an
 * institution by its code** — docs/decisions/0119. A credit statement line
 * whose description, sender or reference names exactly one institution's code
 * (the issue #10 rule), not excluded, and not wholly held. `open` is what no
 * confirmed match, refund or clearing holds yet — a match awaiting a second
 * person still counts as open (docs/decisions/0121). It is **not** part of any
 * ledger balance: confirming a match posts its amount to the cari of the
 * donation's recording institution and takes it off this list in the same
 * transaction, and nothing here books it.
 */
export const institutionPendingMovementSchema = z.object({
  id: z.uuid(),
  bookedOn: isoDaySchema,
  valueDate: isoDaySchema.nullable(),
  amount: moneySchema,
  open: moneySchema,
  description: z.string().nullable(),
  counterpartyName: z.string().nullable(),
  externalRef: z.string().nullable(),
  organizationId: z.uuid(),
  organizationName: z.string(),
  /** Null where the reader's scope does not wholly hold the organization (`organizationInScope`, 0129). */
  organizationAccountId: z.uuid().nullable(),
  accountLabel: z.string().nullable(),
  matchedCode: z.string(),
});

export type InstitutionPendingMovement = z.infer<typeof institutionPendingMovementSchema>;

export const INSTITUTION_PENDING_SORT_FIELDS = ['bookedOn', 'amount'] as const;

export const institutionPendingMovementsInputSchema = paginationInputSchema.extend({
  institutionId: z.uuid(),
  sortBy: z.enum(INSTITUTION_PENDING_SORT_FIELDS).optional(),
  sortDir: z.enum(['asc', 'desc']).optional(),
  currency: currencySchema.optional(),
});

export type InstitutionPendingMovementsInput = z.infer<
  typeof institutionPendingMovementsInputSchema
>;

const pendingTotalSchema = z.object({
  currency: currencySchema,
  movementCount: z.number().int().nonnegative(),
  amount: moneySchema,
  open: moneySchema,
});

/**
 * One page of the institution's pending movements, and beside it the totals of
 * **every** pending movement the reader may see — per currency, and per
 * organization holding the account — never narrowed by the page or the filter.
 */
export const institutionPendingMovementsSchema = paginated(institutionPendingMovementSchema).extend(
  {
    institution: z.object({
      id: z.uuid(),
      code: z.string(),
      name: z.string(),
      countryName: z.string(),
    }),
    totals: z.array(pendingTotalSchema),
    byOrganization: z.array(
      pendingTotalSchema.extend({ organizationId: z.uuid(), organizationName: z.string() }),
    ),
  },
);

export type InstitutionPendingMovements = z.infer<typeof institutionPendingMovementsSchema>;

/** One institution and currency with movements awaiting reconciliation. */
export const institutionPendingSummarySchema = z.object({
  institutionId: z.uuid(),
  institutionCode: z.string(),
  institutionName: z.string(),
  countryName: z.string(),
  currency: currencySchema,
  movementCount: z.number().int().positive(),
  open: moneySchema,
  lastBookedOn: isoDaySchema,
});

export type InstitutionPendingSummary = z.infer<typeof institutionPendingSummarySchema>;

export const institutionPendingSummaryInputSchema = listRequestSchema.extend({
  currency: currencySchema.optional(),
});

export type InstitutionPendingSummaryInput = z.infer<typeof institutionPendingSummaryInputSchema>;

export const financeContract = {
  /** The organizations whose every institution the reader's scope holds. */
  organizations: oc
    .route({ method: 'GET', path: '/finance/organizations' })
    .errors(guarded)
    .input(paginationInputSchema)
    .output(paginated(financeOrganizationSchema)),

  balances: oc
    .route({ method: 'GET', path: '/finance/balances' })
    .errors(guarded)
    .input(balancesInputSchema)
    .output(balancesSchema),

  /**
   * The accounts' figures of the organizations the reader may read, rolled up
   * by the country, zone or region they are placed in — docs/decisions/0127.
   */
  balancesByLocation: oc
    .route({ method: 'GET', path: '/finance/balances-by-location' })
    .errors(guarded)
    .input(balancesByLocationInputSchema)
    .output(paginated(locationBalanceGroupSchema)),

  /**
   * Each currency's movements in the period, and beside them the same in the
   * organization's base currency, each day converted at the last bulletin
   * before it. A currency with no rate is listed unconverted.
   */
  consolidation: oc
    .route({ method: 'GET', path: '/finance/consolidation' })
    .errors(guarded)
    .input(consolidationInputSchema)
    .output(consolidationSchema),

  /**
   * The institutions the reader's scope holds that have an inter-institution
   * line, each with its balance per currency — docs/decisions/0115.
   */
  institutionLedgerBalances: oc
    .route({ method: 'GET', path: '/finance/institution-ledger/balances' })
    .errors(guarded)
    .input(institutionLedgerBalancesInputSchema)
    .output(paginated(institutionLedgerBalanceSchema)),

  /** One institution's statement. An institution outside scope reads `NOT_FOUND`. */
  institutionLedgerStatement: oc
    .route({ method: 'GET', path: '/finance/institution-ledger/institutions/{institutionId}' })
    .errors(guarded)
    .input(institutionLedgerStatementInputSchema)
    .output(institutionLedgerStatementSchema),

  /**
   * One institution's incoming movements classified to it by code and awaiting
   * reconciliation, with their totals — docs/decisions/0119. An institution
   * outside scope reads `NOT_FOUND`.
   */
  institutionPendingMovements: oc
    .route({
      method: 'GET',
      path: '/finance/institution-ledger/institutions/{institutionId}/pending-movements',
    })
    .errors(guarded)
    .input(institutionPendingMovementsInputSchema)
    .output(institutionPendingMovementsSchema),

  /** The institutions the reader may see with movements awaiting reconciliation, per currency. */
  institutionPendingSummary: oc
    .route({ method: 'GET', path: '/finance/institution-ledger/pending-movements' })
    .errors(guarded)
    .input(institutionPendingSummaryInputSchema)
    .output(paginated(institutionPendingSummarySchema)),
};
